<template>
  <!-- Menu "Agendor" do cabeçalho do chat (plano-agendor.md, fase 4). Só pra quem tem acesso (ligado + agente atribuído). -->
  <span v-if="visible" class="agm" :class="[`agm--${variant}`, { 'agm--on': !!link }]">
    <button ref="btn" type="button" class="agm-btn" :title="title" :aria-expanded="open" aria-haspopup="menu" @click.stop="toggle">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M3 13h18" />
      </svg>
      <span>Agendor</span>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
    </button>

    <Teleport to="body">
      <div v-if="open" ref="menu" class="agm-menu" role="menu" :style="menuStyle" @click.stop @keydown.esc="open = false">
        <div class="agm-head">
          <b>{{ link?.personName ?? 'Não vinculado' }}</b>
          <small>{{ link ? [link.organizationName, link.ownerName ? `resp. ${link.ownerName}` : null].filter(Boolean).join(' · ') || 'Agendor' : 'Contato ainda não está no Agendor' }}</small>
        </div>

        <template v-if="!link">
          <button type="button" role="menuitem" class="agm-item" @click="openPanel('send')">Enviar para o Agendor</button>
          <button type="button" role="menuitem" class="agm-item" @click="openPanel('send')">Procurar pelo telefone</button>
        </template>

        <template v-else>
          <button type="button" role="menuitem" class="agm-item" @click="openPanel(null)">Abrir painel do Agendor</button>
          <a v-if="link.webUrl" role="menuitem" class="agm-item" :href="link.webUrl" target="_blank" rel="noopener" @click="open = false">Ver no Agendor ↗</a>

          <div class="agm-sep">Negócio</div>
          <button type="button" role="menuitem" class="agm-item" @click="openPanel('deal')">Criar negócio</button>

          <button v-if="canMove" type="button" role="menuitem" class="agm-item agm-sub" :aria-expanded="sub === 'move'" :disabled="!ongoing.length" @click="toggleSub('move')">
            Mover etapa <span>{{ sub === 'move' ? '▾' : '▸' }}</span>
          </button>
          <div v-if="sub === 'move'" class="agm-nested">
            <template v-for="d in ongoing" :key="d.id">
              <div class="agm-nested-title">{{ d.title }}</div>
              <button v-for="s in stagesOf(d.funnelId)" :key="s.id" type="button" role="menuitem" class="agm-item agm-item--nested"
                      :disabled="busy || d.stageSequence === s.sequence" @click="move(d, s.sequence)">
                {{ d.stageSequence === s.sequence ? '● ' : '' }}{{ s.name }}
              </button>
            </template>
          </div>

          <template v-if="canWinLose">
            <button type="button" role="menuitem" class="agm-item agm-sub" :aria-expanded="sub === 'won'" :disabled="!ongoing.length" @click="toggleSub('won')">
              Marcar ganho <span>{{ sub === 'won' ? '▾' : '▸' }}</span>
            </button>
            <div v-if="sub === 'won'" class="agm-nested">
              <button v-for="d in ongoing" :key="d.id" type="button" role="menuitem" class="agm-item agm-item--nested" :disabled="busy" @click="win(d)">{{ d.title }}</button>
            </div>
            <button type="button" role="menuitem" class="agm-item" :disabled="!ongoing.length" @click="openPanel('deal')">Marcar perdido…</button>
          </template>
          <p v-if="state.loading.value && !panel" class="agm-note">Carregando negócios…</p>

          <div class="agm-sep">Seguir o lead</div>
          <button type="button" role="menuitem" class="agm-item" @click="openPanel('task')">Criar retorno</button>
          <button type="button" role="menuitem" class="agm-item" @click="openPanel('note')">Registrar anotação</button>
          <button v-if="panel?.myUserId && !ownerIsMe" type="button" role="menuitem" class="agm-item" :disabled="busy" @click="assume">Assumir lead</button>
          <button type="button" role="menuitem" class="agm-item agm-sub" :aria-expanded="sub === 'pass'" :disabled="!passTargets.length" @click="toggleSub('pass')">
            Passar lead para <span>{{ sub === 'pass' ? '▾' : '▸' }}</span>
          </button>
          <div v-if="sub === 'pass'" class="agm-nested">
            <button v-for="u in passTargets" :key="u.id" type="button" role="menuitem" class="agm-item agm-item--nested" :disabled="busy" @click="pass(u.id)">{{ u.name }}</button>
          </div>

          <div class="agm-sep"></div>
          <button type="button" role="menuitem" class="agm-item" :disabled="busy || state.loading.value" @click="refresh">Atualizar</button>
        </template>
        <p v-if="state.problem.value" class="agm-problem">{{ state.problem.value }}</p>
      </div>
    </Teleport>

    <AgendorPanel
      v-if="panelOpen && contactId"
      :state="state"
      :contact-id="contactId"
      :conversation-id="conversationId"
      :conversation-agent-id="conversationAgentId"
      :contact-name="contactName"
      :focus="focus"
      @close="panelOpen = false"
    />
  </span>
</template>

<script setup lang="ts">
// Botão "Agendor ▾" com submenus: Mover etapa ▸ (negócio → etapa), Marcar ganho ▸, Passar lead para ▸. O resto abre o
// painel lateral (AgendorPanel) já na seção certa. Os dados vêm do painel (lido quando o menu abre).
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'
import AgendorPanel from '@/components/agendor/AgendorPanel.vue'
import { usePermission } from '@/composables/usePermission'
import { loadAgendor, requestAgendorLink, useAgendor } from '@/composables/useAgendor'
import { useAgendorPanel, type AgendorPanelFocus } from '@/composables/useAgendorPanel'
import { assignConversation } from '@/services/wootdashConversations'
import { findAgendorContact, moveAgendorDeal, setAgendorDealStatus, setAgendorOwner, type AgendorDeal, type AgendorStage } from '@/services/wootdashAgendor'

const props = withDefaults(defineProps<{
  contactId: string | null | undefined
  conversationId?: string | null
  conversationAgentId?: string | null
  contactName?: string | null
  variant?: 'dark' | 'light'
}>(), { conversationId: null, conversationAgentId: null, contactName: null, variant: 'light' })

const store = useStore()
const { can } = usePermission()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')
const { access, links } = useAgendor()
const state = useAgendorPanel(() => token.value, () => props.contactId)

const canMove = computed(() => can('agendor.mover_etapa'))
const canWinLose = computed(() => can('agendor.ganho_perda'))
const link = computed(() => (props.contactId ? links.get(props.contactId) ?? null : null))
const visible = computed(() => !!access.value?.allowed && !!props.contactId)
const panel = computed(() => state.panel.value)
const busy = computed(() => state.busy.value)
const ongoing = computed(() => (panel.value?.deals ?? []).filter(d => d.status === 'ongoing' && d.funnelId))
const ownerIsMe = computed(() => !!panel.value?.myUserId && panel.value.users.find(u => u.id === panel.value!.myUserId)?.name === link.value?.ownerName)
const passTargets = computed(() => (panel.value?.users ?? []).filter(u => u.name !== link.value?.ownerName))
const title = computed(() => link.value
  ? ['Agendor', link.value.personName, link.value.organizationName, link.value.ownerName ? `responsável ${link.value.ownerName}` : null].filter(Boolean).join(' · ')
  : 'Agendor: contato não vinculado')

const open = ref(false)
const sub = ref<'move' | 'won' | 'pass' | null>(null)
const panelOpen = ref(false)
const focus = ref<AgendorPanelFocus>(null)
const btn = ref<HTMLElement | null>(null)
const menu = ref<HTMLElement | null>(null)
const menuStyle = ref<Record<string, string>>({})

function ensure() { void loadAgendor(token.value).then(() => requestAgendorLink(props.contactId)) }
onMounted(ensure)
watch(() => props.contactId, () => { ensure(); open.value = false; panelOpen.value = false; state.panel.value = null })

function place() {
  const r = btn.value?.getBoundingClientRect()
  if (!r) return
  const width = 260
  menuStyle.value = { top: `${r.bottom + 6}px`, left: `${Math.max(8, Math.min(window.innerWidth - width - 8, r.right - width))}px`, width: `${width}px` }
}

function toggle() {
  open.value = !open.value
  sub.value = null
  if (!open.value) return
  place()
  void state.load()
  void nextTick(() => menu.value?.querySelector<HTMLElement>('[role="menuitem"]')?.focus())
}

function toggleSub(name: 'move' | 'won' | 'pass') { sub.value = sub.value === name ? null : name }

function openPanel(section: AgendorPanelFocus) {
  open.value = false
  focus.value = section
  panelOpen.value = true
  if (!panel.value) void state.load()
}

function stagesOf(funnelId: string | null): AgendorStage[] {
  return panel.value?.funnels.find(f => f.id === funnelId)?.stages ?? []
}

async function move(d: AgendorDeal, stage: number) {
  if (await state.run(() => moveAgendorDeal(token.value, props.contactId!, props.conversationId, d.id, d.funnelId!, stage))) open.value = false
}

async function win(d: AgendorDeal) {
  open.value = false
  const ok = await Swal.fire({ title: 'Marcar como ganho?', text: `"${d.title}" vira ganho no Agendor e a conversa ganha a etiqueta de cliente.`, icon: 'question',
    showCancelButton: true, confirmButtonText: 'Marcar ganho', cancelButtonText: 'Cancelar' })
  if (ok.isConfirmed) await state.run(() => setAgendorDealStatus(token.value, props.contactId!, props.conversationId, d.id, 'won'))
}

async function assume() {
  if (await state.run(() => setAgendorOwner(token.value, props.contactId!, props.conversationId, null))) open.value = false
}

async function pass(userId: string) {
  const target = panel.value?.users.find(u => u.id === userId)
  if (!(await state.run(() => setAgendorOwner(token.value, props.contactId!, props.conversationId, userId)))) return
  open.value = false
  if (target?.agentId && props.conversationId && target.agentId.toLowerCase() !== (props.conversationAgentId ?? '').toLowerCase()) {
    const ask = await Swal.fire({ title: `Transferir a conversa para ${target.name} também?`, text: 'O lead já é dele no Agendor.', icon: 'question',
      showCancelButton: true, confirmButtonText: 'Transferir', cancelButtonText: 'Só o lead' })
    if (ask.isConfirmed && !(await assignConversation(token.value, props.conversationId, target.agentId))) {
      state.problem.value = `Não deu pra transferir a conversa para ${target.name}.`
    }
  }
}

async function refresh() {
  await findAgendorContact(token.value, props.contactId!, props.conversationId)
  await state.load(true)
}

function onOutside(e: MouseEvent) {
  if (open.value && !menu.value?.contains(e.target as Node) && !btn.value?.contains(e.target as Node)) open.value = false
}
onMounted(() => { document.addEventListener('click', onOutside); window.addEventListener('scroll', place, true) })
onBeforeUnmount(() => { document.removeEventListener('click', onOutside); window.removeEventListener('scroll', place, true) })
</script>

<style scoped>
.agm { display: inline-flex; align-items: center; vertical-align: middle; }
.agm-btn {
  display: inline-flex; align-items: center; gap: 5px; padding: 3px 8px; border-radius: 6px; cursor: pointer; font: inherit; font-size: 11.5px; font-weight: 700;
  border: 1px dashed currentColor; background: transparent; color: #94a3b8;
}
.agm--on .agm-btn { border-style: solid; border-color: transparent; background: #e0f0ed; color: #0f6f66; }
.agm--dark .agm-btn { color: rgba(255, 255, 255, .75); }
.agm--dark.agm--on .agm-btn { background: rgba(79, 193, 179, .2); color: #8be3d6; }
.agm-btn:focus-visible { outline: 2px solid #0f6f66; outline-offset: 1px; }
.agm-menu {
  position: fixed; z-index: 3995; background: var(--white, #fff); color: var(--ink-80, #1f2937); border: 1px solid var(--ink-10, #e5e7eb); border-radius: 10px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, .18); padding: 6px; display: flex; flex-direction: column; font-size: 13px; max-height: 75vh; overflow-y: auto;
}
.agm-head { display: flex; flex-direction: column; gap: 1px; padding: 6px 8px 8px; border-bottom: 1px solid var(--ink-05, #f3f4f6); margin-bottom: 4px; }
.agm-head small { color: var(--ink-40, #6b7280); font-size: 11.5px; }
.agm-item {
  display: flex; justify-content: space-between; align-items: center; width: 100%; text-align: left; border: none; background: none; color: inherit;
  font: inherit; font-size: 13px; padding: 7px 8px; border-radius: 6px; cursor: pointer; text-decoration: none; box-sizing: border-box;
}
.agm-item:hover:not(:disabled), .agm-item:focus-visible { background: var(--ink-05, #f3f4f6); outline: none; }
.agm-item:disabled { opacity: .45; cursor: not-allowed; }
.agm-sub span { color: var(--ink-40, #9ca3af); font-size: 11px; }
.agm-nested { display: flex; flex-direction: column; margin: 0 0 4px 10px; padding-left: 8px; border-left: 2px solid #e0f0ed; }
.agm-nested-title { font-size: 11px; font-weight: 700; color: var(--ink-40, #6b7280); padding: 6px 8px 2px; text-transform: uppercase; letter-spacing: .03em; }
.agm-item--nested { font-size: 12.5px; padding: 5px 8px; }
.agm-sep { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: var(--ink-40, #9ca3af); padding: 8px 8px 3px; border-top: 1px solid var(--ink-05, #f3f4f6); margin-top: 4px; }
.agm-sep:empty { padding: 0; margin: 4px 0; }
.agm-note { margin: 0; padding: 4px 8px; color: var(--ink-40, #6b7280); font-size: 12px; }
.agm-problem { margin: 6px 4px 2px; color: #b45309; font-size: 12px; }
</style>
